/**
 * Item de background job en la campanita. Deslizar a la izquierda → Quitar
 * (descarta la notificación: setea dismissed_at vía el realtime del padre).
 */
import { CheckCircle2, Loader2, Sparkles, Trash2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { SwipeableRow } from '@/components/ui/swipeable-row';
import { cn } from '@/lib/utils';
import type { BackgroundJob } from '@/services/backgroundJobsService';

import { TiempoRelativo } from './TiempoRelativo';

const JOB_KIND_LABEL: Record<BackgroundJob['kind'], string> = {
  analyze_prospect_deep: 'Análisis profundo de prospect',
  analyze_prospect_deep_v2: 'Deep Research v2 de prospect',
  generate_campaign_deep: 'Generando campaña IA',
  bulk_prospect_enrichment: 'Enriqueciendo prospects en lote',
};

interface NotificationJobItemProps {
  job: BackgroundJob;
  onNavigate: () => void;
  /** Quitar de la campanita (descarta el job). */
  onDismiss: () => void;
}

export function NotificationJobItem({ job, onNavigate, onDismiss }: NotificationJobItemProps) {
  const isRunning = job.status === 'running' || job.status === 'pending';
  const isDone = job.status === 'done';
  const isError = job.status === 'error';
  const isCancelled = job.status === 'cancelled';

  const title = JOB_KIND_LABEL[job.kind] ?? job.kind;
  const subtitle = isRunning
    ? job.progress_step ?? 'Procesando…'
    : isDone
      ? 'Listo'
      : isError
        ? job.error_json?.message?.slice(0, 120) ?? 'Falló'
        : isCancelled
          ? 'Cancelado'
          : '';

  const link = isDone
    ? job.kind === 'analyze_prospect_deep'
      ? `/prospects/new/from-job/${job.id}`
      : job.related_prospect_id
        ? `/prospects/${job.related_prospect_id}`
        : job.related_campaign_id
          ? `/campaigns/${job.related_campaign_id}`
          : null
    : null;

  const linkLabel =
    job.kind === 'analyze_prospect_deep' ? 'Revisar y crear prospect' : 'Abrir';

  const itemClass = cn(
    'flex items-start gap-3 px-3.5 py-2.5 transition-colors',
    link ? 'hover:bg-surface-hi/60 cursor-pointer' : '',
  );

  const inner = (
    <>
      <div
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
          isRunning && 'bg-primary/15 text-primary',
          isDone && 'bg-emerald-500/15 text-emerald-400',
          isError && 'bg-destructive/15 text-destructive',
          isCancelled && 'bg-muted text-fg-muted',
        )}
      >
        {isRunning ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : isDone ? (
          <CheckCircle2 className="h-3.5 w-3.5" />
        ) : isError ? (
          <XCircle className="h-3.5 w-3.5" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-foreground truncate text-caption font-semibold">
            {title}
          </p>
          <TiempoRelativo iso={job.updated_at ?? job.created_at} />
        </div>
        <p className="text-muted-foreground mt-0.5 break-words text-micro leading-snug">
          {subtitle}
        </p>
        {isRunning && typeof job.progress_pct === 'number' ? (
          <div className="bg-muted mt-1.5 h-1 w-full overflow-hidden rounded-full">
            <div
              className="bg-primary h-full transition-all duration-300"
              style={{ width: `${job.progress_pct}%` }}
            />
          </div>
        ) : null}
        {link && (
          <span className="text-primary mt-1.5 inline-flex items-center gap-1 text-micro font-medium">
            {linkLabel} →
          </span>
        )}
      </div>
    </>
  );

  return (
    <li className="border-border-dim border-b last:border-0">
      <SwipeableRow
        contentClassName="bg-popover"
        hoverActionBg="var(--popover)"
        endAction={{
          icon: Trash2,
          label: 'Quitar',
          bg: 'bg-destructive',
          onAction: onDismiss,
          destructive: true,
        }}
      >
        {link ? (
          <Link to={link} onClick={onNavigate} className={itemClass}>
            {inner}
          </Link>
        ) : (
          <div className={itemClass}>{inner}</div>
        )}
      </SwipeableRow>
    </li>
  );
}
